/**
 * DayTimeline - Execution-Focused Day View
 * 
 * PRIMARY PURPOSE: Help athletes COMPLETE their day, not just view it.
 * 
 * Design Philosophy:
 * - Every item is actionable (complete, log, check-in)
 * - Progress is visible at a glance
 * - Current/next activity is highlighted
 * - Completed items show success state
 * - Edit is secondary, completion is primary
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../App';
import { useExerciseLibrary } from '../context/ExerciseLibraryContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { 
  Clock, Coffee, Sun, Moon, Sunrise, Dumbbell, Heart, Zap,
  AlertTriangle, ChevronRight, ChevronDown, Check, X, Info,
  Calendar, MapPin, Play, Utensils, Bed, Target, Activity,
  CheckCircle2, Circle, Flame, Award, MoreHorizontal
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Icon mapping for timeline items
const ICON_MAP = {
  sunrise: Sunrise,
  sun: Sun,
  moon: Moon,
  coffee: Coffee,
  dumbbell: Dumbbell,
  heart: Heart,
  zap: Zap,
  bed: Bed,
  calendar: Calendar,
  target: Target,
  activity: Activity,
};

const DayTimeline = ({ date, onPointsEarned }) => {
  const navigate = useNavigate();
  const { showExerciseDetail, findExercise } = useExerciseLibrary();
  
  const [timeline, setTimeline] = useState([]);
  const [summary, setSummary] = useState(null);
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemDetail, setShowItemDetail] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  
  // Completion tracking
  const [completedItems, setCompletedItems] = useState({});
  const [completedExercises, setCompletedExercises] = useState({});
  const [todayPoints, setTodayPoints] = useState(0);
  
  const today = date || format(new Date(), 'yyyy-MM-dd');
  
  useEffect(() => {
    fetchTimeline();
    fetchCompletedActivities();
  }, [today]);
  
  const fetchTimeline = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/intelligence/day-timeline?date=${today}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTimeline(response.data.timeline || []);
      setSummary(response.data.summary || {});
      setRiskAssessment(response.data.risk_assessment || {});
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
      toast.error('Failed to load day timeline');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCompletedActivities = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      
      // Fetch from daily activity logs
      const activityResponse = await axios.get(`${API}/hydration/activity/today`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { completed_activities: [], total_points: 0 } }));
      
      // Build completion maps from activity log
      const completed = {};
      const exercises = {};
      
      const activities = activityResponse.data?.completed_activities || [];
      activities.forEach(activity => {
        if (activity.type === 'exercise') {
          exercises[activity.key] = true;
        } else {
          completed[activity.key] = true;
        }
      });
      
      setCompletedItems(completed);
      setCompletedExercises(exercises);
      setTodayPoints(activityResponse.data?.total_points || 0);
    } catch (error) {
      console.error('Failed to fetch completed activities:', error);
    }
  };
  
  // Complete an entire timeline item (routine, meal window, etc.)
  const completeTimelineItem = async (itemId, itemType, itemTitle) => {
    try {
      const token = localStorage.getItem('aura_token');
      
      await axios.post(`${API}/hydration/activity/complete`, {
        activity_type: itemType,
        activity_key: itemId,
        activity_name: itemTitle || itemType,
        points: 5
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCompletedItems(prev => ({ ...prev, [itemId]: true }));
      setTodayPoints(prev => prev + 5);
      toast.success(`${itemTitle || 'Activity'} complete! +5 pts`);
      
      if (onPointsEarned) onPointsEarned(5);
    } catch (error) {
      console.error('Failed to complete activity:', error);
      toast.error('Failed to log completion');
    }
  };
  
  // Complete an individual exercise within a workout
  const completeExercise = async (itemId, exerciseIndex, exerciseName) => {
    const exerciseKey = `${itemId}_exercise_${exerciseIndex}`;
    
    try {
      const token = localStorage.getItem('aura_token');
      
      await axios.post(`${API}/hydration/activity/complete`, {
        activity_type: 'exercise',
        activity_key: exerciseKey,
        activity_name: exerciseName,
        points: 10
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCompletedExercises(prev => ({ ...prev, [exerciseKey]: true }));
      setTodayPoints(prev => prev + 10);
      toast.success(`${exerciseName} done! +10 pts`);
      
      if (onPointsEarned) onPointsEarned(10);
    } catch (error) {
      console.error('Failed to complete exercise:', error);
      toast.error('Failed to log exercise');
    }
  };
  
  const toggleItemExpanded = (itemId) => {
    setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };
  
  const handleExerciseInfo = (exerciseName) => {
    const exercise = findExercise(exerciseName);
    if (exercise) {
      showExerciseDetail(exercise);
    }
  };
  
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };
  
  const getItemIcon = (item) => {
    return ICON_MAP[item.icon] || Calendar;
  };
  
  const isCurrentTimeNear = (startTime, endTime) => {
    const now = new Date();
    const [startHours, startMinutes] = startTime.split(':');
    const [endHours, endMinutes] = endTime.split(':');
    
    const start = new Date();
    start.setHours(parseInt(startHours), parseInt(startMinutes), 0);
    
    const end = new Date();
    end.setHours(parseInt(endHours), parseInt(endMinutes), 0);
    
    return now >= start && now <= end;
  };
  
  const isPast = (endTime) => {
    const now = new Date();
    const [hours, minutes] = endTime.split(':');
    const itemEnd = new Date();
    itemEnd.setHours(parseInt(hours), parseInt(minutes), 0);
    return now > itemEnd;
  };
  
  const isUpcoming = (startTime) => {
    const now = new Date();
    const [hours, minutes] = startTime.split(':');
    const itemStart = new Date();
    itemStart.setHours(parseInt(hours), parseInt(minutes), 0);
    
    const diff = (itemStart - now) / (1000 * 60); // minutes until
    return diff > 0 && diff < 60; // Within next hour
  };
  
  // Calculate completion stats
  const getCompletionStats = () => {
    const completableItems = timeline.filter(item => 
      ['routine', 'meal_window', 'workout', 'auto_generated'].includes(item.type)
    );
    const completedCount = completableItems.filter(item => completedItems[item.id]).length;
    return {
      total: completableItems.length,
      completed: completedCount,
      percentage: completableItems.length > 0 ? Math.round((completedCount / completableItems.length) * 100) : 0
    };
  };
  
  // Get exercise completion count for a workout
  const getExerciseCompletionCount = (itemId, exercises) => {
    if (!exercises) return { completed: 0, total: 0 };
    const completed = exercises.filter((_, idx) => 
      completedExercises[`${itemId}_exercise_${idx}`]
    ).length;
    return { completed, total: exercises.length };
  };
  
  const completionStats = getCompletionStats();
  
  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-20 bg-white/5 rounded-lg"></div>
        ))}
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Day Progress Header */}
      <Card className="bg-gradient-to-r from-aura-primary/20 to-purple-500/20 border-aura-primary/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-aura-primary/30 flex items-center justify-center">
                <Flame className="w-6 h-6 text-aura-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Today's Progress</p>
                <p className="text-2xl font-bold text-aura-primary">
                  {completionStats.completed}/{completionStats.total} Complete
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 text-amber-400">
                <Award className="w-4 h-4" />
                <span className="text-lg font-bold">{todayPoints}</span>
              </div>
              <p className="text-xs text-aura-text-muted">pts earned</p>
            </div>
          </div>
          <Progress value={completionStats.percentage} className="h-2" />
          <p className="text-xs text-aura-text-muted mt-2 text-center">
            {completionStats.percentage}% of your day complete
          </p>
        </CardContent>
      </Card>
      
      {/* Risk Alert (if applicable) */}
      {riskAssessment?.level && riskAssessment.level !== 'low' && (
        <Card className={`border ${
          riskAssessment.level === 'high' ? 'bg-red-500/10 border-red-500/30' : 'bg-yellow-500/10 border-yellow-500/30'
        }`}>
          <CardContent className="p-3 flex items-start gap-2">
            <AlertTriangle className={`w-4 h-4 mt-0.5 ${
              riskAssessment.level === 'high' ? 'text-red-400' : 'text-yellow-400'
            }`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                riskAssessment.level === 'high' ? 'text-red-400' : 'text-yellow-400'
              }`}>
                {riskAssessment.level === 'high' ? 'High Load Day' : 'Moderate Load'}
              </p>
              {riskAssessment.notes?.map((note, i) => (
                <p key={i} className="text-xs text-aura-text-muted">{note}</p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Timeline Items */}
      <div className="space-y-2">
        {timeline.map((item, index) => {
          const ItemIcon = getItemIcon(item);
          const isCompleted = completedItems[item.id];
          const isCurrent = isCurrentTimeNear(item.start_time, item.end_time);
          const itemPast = isPast(item.end_time);
          const itemUpcoming = isUpcoming(item.start_time);
          const isExpanded = expandedItems[item.id];
          const hasExercises = item.exercises && item.exercises.length > 0;
          const exerciseStats = hasExercises ? getExerciseCompletionCount(item.id, item.exercises) : null;
          
          // Determine if this item can be completed
          const canComplete = ['routine', 'meal_window', 'workout', 'auto_generated'].includes(item.type);
          
          return (
            <Card 
              key={item.id || index}
              className={`border transition-all overflow-hidden ${
                isCompleted 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : isCurrent 
                  ? 'bg-aura-primary/10 border-aura-primary/50 ring-1 ring-aura-primary/50' 
                  : itemUpcoming
                  ? 'bg-yellow-500/5 border-yellow-500/30'
                  : itemPast 
                  ? 'bg-white/5 border-white/10 opacity-60' 
                  : 'bg-aura-surface border-white/10'
              }`}
            >
              <CardContent className="p-0">
                {/* Main Item Row */}
                <div className="p-3 flex items-center gap-3">
                  {/* Time */}
                  <div className="w-14 text-center">
                    <p className={`text-xs font-mono ${
                      isCurrent ? 'text-aura-primary font-bold' : 'text-aura-text-muted'
                    }`}>
                      {formatTime(item.start_time)}
                    </p>
                  </div>
                  
                  {/* Completion Button / Status */}
                  {canComplete && (
                    <button
                      onClick={() => !isCompleted && completeTimelineItem(item.id, item.type, item.title)}
                      disabled={isCompleted}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                        isCompleted 
                          ? 'bg-green-500 text-white cursor-default' 
                          : 'bg-white/10 hover:bg-white/20 text-white hover:scale-110'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <Circle className="w-5 h-5" />
                      )}
                    </button>
                  )}
                  
                  {/* Non-completable items show icon */}
                  {!canComplete && (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      item.block_type === 'class' ? 'bg-slate-500/30' :
                      item.block_type === 'practice' ? 'bg-orange-500/30' :
                      item.block_type === 'game' ? 'bg-red-500/30' :
                      'bg-white/10'
                    }`}>
                      <ItemIcon className="w-4 h-4" />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium truncate ${
                        isCompleted ? 'text-green-400 line-through' : 'text-white'
                      }`}>
                        {item.title}
                      </p>
                      {isCurrent && !isCompleted && (
                        <Badge className="bg-aura-primary/30 text-aura-primary text-xs animate-pulse">
                          NOW
                        </Badge>
                      )}
                      {itemUpcoming && !isCompleted && (
                        <Badge className="bg-yellow-500/30 text-yellow-400 text-xs">
                          Up Next
                        </Badge>
                      )}
                    </div>
                    
                    {/* Quick info */}
                    <div className="flex items-center gap-2 mt-0.5">
                      {item.duration_minutes && (
                        <span className="text-xs text-aura-text-muted">
                          {item.duration_minutes} min
                        </span>
                      )}
                      {exerciseStats && (
                        <span className={`text-xs ${
                          exerciseStats.completed === exerciseStats.total ? 'text-green-400' : 'text-aura-text-muted'
                        }`}>
                          {exerciseStats.completed}/{exerciseStats.total} exercises
                        </span>
                      )}
                      {item.intensity && item.intensity !== 'none' && item.intensity !== 'unknown' && (
                        <Badge variant="outline" className={`text-xs border-white/20 ${
                          item.intensity === 'high' ? 'text-red-400 border-red-400/30' :
                          item.intensity === 'low' ? 'text-green-400 border-green-400/30' :
                          'text-white/70'
                        }`}>
                          {item.intensity}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Expand button for items with exercises */}
                  {hasExercises && (
                    <button
                      onClick={() => toggleItemExpanded(item.id)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-aura-text-muted" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-aura-text-muted" />
                      )}
                    </button>
                  )}
                  
                  {/* Points indicator */}
                  {canComplete && !isCompleted && (
                    <div className="text-right">
                      <span className="text-xs text-amber-400">+{hasExercises ? '10' : '5'}</span>
                    </div>
                  )}
                  {isCompleted && (
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                  )}
                </div>
                
                {/* Activities list for routines */}
                {item.activities && !hasExercises && (
                  <div className="px-3 pb-3 pl-[4.5rem]">
                    <div className="flex flex-wrap gap-1">
                      {item.activities.map((activity, i) => (
                        <span key={i} className="text-xs text-aura-text-muted bg-white/5 px-2 py-0.5 rounded">
                          {activity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Expanded Exercise List */}
                {hasExercises && isExpanded && (
                  <div className="border-t border-white/10 bg-black/20">
                    <div className="p-3 space-y-1">
                      {item.exercises.map((exercise, idx) => {
                        const exerciseKey = `${item.id}_${idx}`;
                        const isExerciseCompleted = completedExercises[exerciseKey];
                        const hasLibraryEntry = findExercise(exercise.name);
                        
                        return (
                          <div 
                            key={idx}
                            className={`flex items-center gap-2 p-2 rounded-lg transition-all ${
                              isExerciseCompleted 
                                ? 'bg-green-500/10' 
                                : 'hover:bg-white/5'
                            }`}
                          >
                            <button
                              onClick={() => !isExerciseCompleted && completeExercise(item.id, idx, exercise.name)}
                              disabled={isExerciseCompleted}
                              className={`w-6 h-6 rounded flex items-center justify-center transition-all ${
                                isExerciseCompleted 
                                  ? 'bg-green-500 text-white cursor-default' 
                                  : 'bg-white/10 hover:bg-white/20 text-white'
                              }`}
                            >
                              {isExerciseCompleted ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <span className="text-xs">{idx + 1}</span>
                              )}
                            </button>
                            
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${
                                isExerciseCompleted ? 'text-green-400 line-through' : 'text-white'
                              }`}>
                                {exercise.name}
                              </p>
                              <p className="text-xs text-aura-text-muted">
                                {exercise.sets ? `${exercise.sets} x ${exercise.reps}` : 
                                 exercise.duration ? `${exercise.duration} min` : ''}
                              </p>
                            </div>
                            
                            {hasLibraryEntry && (
                              <button
                                onClick={() => handleExerciseInfo(exercise.name)}
                                className="p-1.5 hover:bg-white/10 rounded transition-colors"
                                title="View how-to video"
                              >
                                <Info className="w-4 h-4 text-blue-400" />
                              </button>
                            )}
                            
                            {!isExerciseCompleted && (
                              <span className="text-xs text-amber-400">+10</span>
                            )}
                            {isExerciseCompleted && (
                              <span className="text-xs text-green-400">Done!</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Meal notes */}
                {item.notes && item.notes.length > 0 && (
                  <div className="px-3 pb-3 pl-[4.5rem]">
                    {item.notes.map((note, i) => (
                      <p key={i} className="text-xs text-yellow-400 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {note}
                      </p>
                    ))}
                  </div>
                )}
                
                {/* Goal note for meals */}
                {item.goal_note && (
                  <div className="px-3 pb-3 pl-[4.5rem]">
                    <p className="text-xs text-amber-400 flex items-center gap-1">
                      <Target className="w-3 h-3" />
                      {item.goal_note} • Target: {item.target_calories} cal
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Bottom Action */}
      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-4 text-center">
          <p className="text-sm text-aura-text-muted mb-2">
            Need to adjust your schedule?
          </p>
          <Button
            variant="outline"
            size="sm"
            className="border-white/20"
            onClick={() => navigate('/calendar-hub')}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Edit in Calendar
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default DayTimeline;
